import type { Resources } from "./zh";

// 英文文案与 zh.ts 结构同构（由 Resources 类型强制键齐全）。
export const en: Resources = {
  common: {
    nav: { home: "Home", products: "Products", about: "Company", contact: "Contact" },
    actions: {
      cooperate: "Start a project",
      viewAllProjects: "View all projects",
      learnTeam: "Meet the team",
      viewProducts: "View products",
    },
    cta: {
      default: {
        title: "Let's grow the value of your energy assets",
        description:
          "With integrated hardware-software solutions and a rich algorithm library, we take on energy-saving projects and smart O&M retrofits — let's talk about your energy digitalization needs.",
      },
      products: {
        title: "Discuss your requirements",
        description:
          "From energy platforms and smart hardware to algorithms and O&M retrofits, we deliver end-to-end — from solution design to deployment and ongoing operation.",
      },
      about: {
        title: "Learn more about Furtherverse",
        description:
          "We welcome conversations around technical cooperation, joint development and academic exchange.",
      },
    },
    theme: { toggle: "Toggle theme", toLight: "Switch to light", toDark: "Switch to dark" },
    language: { switch: "Switch language" },
    footer: {
      tagline:
        "A digital and intelligent enabler for energy systems, providing integrated hardware-software solutions and algorithm resources.",
      nav: "Navigation",
      contact: "Contact",
    },
    lightbox: { zoom: "View {{name}} full image", close: "Close" },
  },

  home: {
    hero: {
      slides: [
        {
          title: ["Deconstructing complex networks", "Awakening native intelligence"],
          subtitle:
            "Reshaping isolated energy nodes into a self-evolving network capable of perception and decision-making.",
        },
        {
          title: ["Piercing hardware-software barriers", "Forging an integrated mind"],
          subtitle:
            "From edge sensing and core algorithms to global command, we insist on full-chain in-house R&D to build an insurmountable technological moat.",
        },
        {
          title: ["Breaking efficiency limits", "Elevating asset value"],
          subtitle:
            "In power, distribution, HVAC and storage scenarios, achieving a leap in reliable operation and commercial returns through precise algorithmic optimization.",
        },
      ],
      primaryCta: "Explore solutions",
      secondaryCta: "Contact us",
      badge: "Digital & intelligent enabler for energy systems",
      proof: [
        { value: "Full-stack", label: "End-to-end in-house R&D" },
        { value: "15+", label: "Efficiency & dispatch algorithms" },
        { value: "All scenarios", label: "Power · Distribution · HVAC · Storage" },
      ],
    },
    capabilities: {
      eyebrow: "Integrated HW/SW · Algorithm-driven",
      heading: "Empowering energy systems with integrated HW/SW and algorithms",
      intro:
        "Across the full lifecycle of energy assets, we provide integrated capabilities — from smart hardware and energy platforms to algorithms and operations — continuously improving efficiency and asset value.",
      items: [
        {
          title: "Integrated HW/SW solutions",
          desc: "Efficiently integrating sensing hardware, edge computing and energy platforms — fast to deploy and dependable.",
        },
        {
          title: "Rich algorithm library",
          desc: "Reusable algorithms for efficiency assessment, prediction, optimal scheduling and operation control.",
        },
        {
          title: "Energy-saving projects",
          desc: "Efficiency diagnosis and energy-saving retrofits that cut consumption and cost, driven by data.",
        },
        {
          title: "Smart O&M retrofits",
          desc: "Bringing remote monitoring, smart diagnosis and automatic scheduling to existing energy assets.",
        },
      ],
    },
    arch: {
      titleBefore: "Constructing a ",
      titleHighlight: "self-evolving",
      titleAfter: " energy agent",
      subtitle:
        "Breaking system silos to let data, algorithms and hardware resonate within a unified architecture, locking in the optimal energy-efficiency path.",
    },
    focus: {
      eyebrow: "The value we create",
      heading: "Making energy more efficient with data and algorithms",
      intro:
        "Across power, distribution, HVAC, storage and other energy scenarios, we use integrated HW/SW solutions and an algorithm library to drive efficiency, smart operations and energy-saving retrofits — growing the value of energy assets.",
      points: [
        "Efficiency monitoring & diagnosis",
        "Smart scheduling & control",
        "Energy-saving retrofits",
        "Smart operations",
      ],
      note: "Whatever the energy scenario, we create value from data.",
      imageAlt: "Energy data dashboard",
      badge: { value: "Real-time", label: "Efficiency monitoring & alerts" },
    },
    stats: {
      products: "In-house products & platforms",
      algorithms: "Efficiency & scheduling algorithms",
      partners: "Partners & institutions",
      directions: "Core business areas",
    },
    featured: { eyebrow: "Selected cases", heading: "Energy digitalization, delivered" },
  },

  arch: {
    layers: { llm: "LLM", app: "Applications", algo: "Algorithm Library", edge: "Edge & Hardware" },
    cta: "Explore the full architecture",
    concept: { llm: "Global Command", app: "Synergistic Hub", algo: "Mechanistic Cognition", edge: "Micro-Sensing" },
    desc: {
      llm: "Industry large models driving complex scenarios, continuously deducing and locking in globally optimal decisions amid massive, multi-dimensional variables.",
      app: "A hardware-software platform carrying core business logic, turning intelligent commands into tangible efficiency gains and reliable operations.",
      algo: "A proprietary algorithm library built on deep industry experience, decoding the physical laws behind power and HVAC flows.",
      edge: "Edge hardware and sensor networks reaching into physical infrastructure, precisely mapping the real-time state of energy assets.",
    },
    llmModules: ["Smart Q&A", "Root-cause diagnosis", "Decision scheduling", "Auto reports"],
    appModules: ["Monitoring center", "Efficiency analysis", "Smart O&M", "Virtual power plant"],
    algoModules: ["Efficiency assessment", "Load forecasting", "Optimal dispatch", "Operation control"],
    edgeModules: ["Smart controller", "Distribution cabinet", "Energy gateway", "Multi-protocol access"],
  },

  products: {
    banner: {
      title: "Products & Solutions",
      eyebrow: "Integrated HW/SW · Algorithm-driven",
      subtitle:
        "For the digitalization and intelligence of energy systems — integrated HW/SW solutions and an algorithm library covering efficiency, energy-saving retrofits and smart operations.",
    },
    archSection: {
      eyebrow: "Platform architecture",
      title: "Pure foundational reconstruction, not a system puzzle",
      subtitle:
        "True intelligence cannot be achieved through multi-party compromise and stacking; we ensure absolute cohesion from perception to decision through a purely in-house developed system.",
    },
    catSection: { eyebrow: "Products / Cases", title: "Solution categories" },
    categoryLabels: {
      all: "All",
      smartEnergy: "Smart Energy",
      productRnD: "Product R&D",
      aiService: "AI Services",
    },
    categoryIntro: {
      smartEnergy:
        "Energy management backend, energy data dashboards, smart controllers, distribution cabinets and more — covering monitoring, analysis, alerting and saving optimization.",
      productRnD:
        "Custom and joint development across software / embedded / hardware — representative deliverables such as edge IPCs and energy gateways, including cross-industry projects.",
      aiService:
        "LLM integration and application services that help enterprises quickly bring AI into their business systems.",
    },
    items: {
      pvPlatform: {
        name: "Energy Management Platform (Dashboard)",
        desc: "A comprehensive efficiency dashboard for plants and parks, showing generation and load power, equipment efficiency, storage and grid status, and consumption analysis at a glance.",
        tags: ["Software platform", "Data visualization", "Energy"],
      },
      energyBackend: {
        name: "Energy Management Backend",
        desc: "A web-based energy management system covering real-time monitoring, device management, consumption analysis, alerting and reporting, with department/sub-item comparison and peak-valley pricing strategies.",
        tags: ["Software platform", "IoT management", "Consumption analysis"],
      },
      chillerDashboard: {
        name: "Chiller O&M Dashboard",
        desc: "An operation dashboard for central-AC chillers, showing real-time status and consumption of chillers, chilled/cooling pumps and cooling towers, with COP analysis and alerting for energy-saving smart maintenance.",
        tags: ["Software platform", "Data visualization", "HVAC"],
      },
      pvController: {
        name: "Smart PV Controller",
        desc: "An outdoor-grade smart PV control/combiner device with integrated data acquisition and communication, reporting to the platform to improve generation efficiency and remote operations.",
        tags: ["Smart hardware", "Embedded", "Solar"],
      },
      switchgear: {
        name: "Smart Distribution Cabinet",
        desc: "A smart cabinet integrating circuit protection, relay control, data acquisition and remote communication, supporting loop monitoring, anomaly alerting and remote interlocking — turning distribution from passive maintenance to proactive intelligence.",
        tags: ["Smart hardware", "Distribution", "IoT"],
      },
      edgeGateway: {
        name: "Edge IPC / Energy Gateway",
        desc: "A fanless industrial edge host with multiple network and serial (RS232/485) ports for multi-protocol device access and local computing — a data hub between field devices and the cloud, stable even on weak or offline networks.",
        tags: ["Product R&D", "Edge computing", "Gateway"],
      },
      llmService: {
        name: "LLM Integration Service",
        desc: "Integration and deployment services for mainstream LLMs — covering model selection, private deployment, knowledge bases, intelligent Q&A and agent workflows — to quickly bring AI into business systems.",
        tags: ["AI Services", "LLM", "Application integration"],
      },
    },
    process: {
      eyebrow: "How we work",
      title: "From requirements to delivery, in five steps",
      steps: [
        { title: "Requirements", desc: "Understand the scenario, goals and scope." },
        { title: "Solution design", desc: "Hardware/software selection and architecture." },
        { title: "Development", desc: "Embedded, platform and algorithms in parallel." },
        { title: "Testing & delivery", desc: "On-site integration and stability validation." },
        { title: "Operation", desc: "Continuous optimization and feature evolution." },
      ],
    },
  },

  about: {
    banner: {
      title: "About Furtherverse",
      eyebrow: "Shanghai Furtherverse",
      subtitle:
        "A digital and intelligent enabler for energy systems — growing the value of energy assets with integrated HW/SW solutions and algorithm resources.",
    },
    intro: {
      eyebrow: "Who we are",
      heading: "A digital and intelligent enabler for energy systems",
      p1: "Furtherverse focuses on the digitalization and intelligence of energy systems, helping clients improve efficiency, optimize operations and grow the value of their energy assets through integrated hardware-software solutions and a rich algorithm library.",
      p2: "We take on energy-saving projects and smart O&M retrofits across power, distribution, HVAC, storage and other energy scenarios, driving continuous optimization with data and algorithms.",
      imageAlt: "Furtherverse technical capabilities",
    },
    advantagesSection: { eyebrow: "Core strengths", heading: "Why choose us" },
    advantages: [
      {
        title: "Integrated HW/SW solutions",
        desc: "Sensing hardware, edge and platform integrated efficiently — fast to deploy and dependable.",
      },
      {
        title: "Rich algorithm library",
        desc: "Algorithms for efficiency, prediction, optimal scheduling and control shorten delivery cycles.",
      },
      {
        title: "Energy saving & O&M retrofits",
        desc: "Efficiency diagnosis, energy-saving retrofits and smart operations that cut consumption with data.",
      },
      {
        title: "Academic & institutional ties",
        desc: "Ongoing collaboration with universities and research institutes such as ECNU.",
      },
    ],
    partnersSection: { eyebrow: "Partners / Academia", heading: "Long-term partners & institutions" },
    partners: {
      zxx: "Zhixinxin",
      runda: "Runda PV",
      enxi: "Evirth",
      mps: "TRIMPS (Third Research Institute of MPS)",
      ecnu: "East China Normal University",
    },
  },

  contact: {
    banner: {
      title: "Contact Us",
      eyebrow: "Business & technical cooperation",
      subtitle: "For project development or technical cooperation, reach us through any of the channels below.",
    },
    email: { label: "Email" },
    phone: { label: "Phone", role: "Business Director · Wang Chenxi" },
    wechat: { label: "WeChat", hint: "Click to enlarge · Scan to add", modalHint: "Scan to add on WeChat" },
  },
};
